/**
 * <hh-scroll-to-buy> wraps a link. On click it scrolls back to the buy box
 * on the current page instead of following the link, so whatever the
 * shopper already picked (for example the bundle option) stays selected.
 * With `data-select-set="2"` it also selects that set in the set picker
 * first (its selectSet()), e.g. The Heir Set from the offer note.
 * If the page has no buy box, the link works as a normal link.
 */
class HHScrollToBuy extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (this.dataset.mode !== 'scroll') return;

    const target = HHScrollToBuy.findBuyBox();
    if (!target) return;

    event.preventDefault();

    const quantity = Number(this.dataset.selectSet);
    if (quantity && 'selectSet' in target && typeof target.selectSet === 'function') {
      target.selectSet(quantity);
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    HHScrollToBuy.#boxOf(target).scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: target.hasAttribute('data-hh-buy-box') ? 'start' : 'center',
    });

    // Move keyboard focus to the buy box without a second scroll jump.
    const focusTarget = target.querySelector('input:checked, button[name="add"], [name="add"]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  };

  /**
   * On phones the set picker uses `display: contents` (its parts join the
   * product details column), so it has no box of its own to scroll to.
   * Then the first child that has one stands in for it.
   * @param {HTMLElement} element
   * @returns {Element}
   */
  static #boxOf(element) {
    if (element.getClientRects().length) return element;
    return Array.from(element.children).find((child) => child.getClientRects().length) ?? element;
  }

  /** @returns {HTMLElement | null} */
  static findBuyBox() {
    const marked = document.querySelector('[data-hh-buy-box]');
    if (marked instanceof HTMLElement) return marked;

    const buyButtons = document.querySelector('product-form-component')?.closest('.buy-buttons-block');
    return buyButtons instanceof HTMLElement ? buyButtons : null;
  }
}

if (!customElements.get('hh-scroll-to-buy')) {
  customElements.define('hh-scroll-to-buy', HHScrollToBuy);
}
